import { Box, Icon, IconButton, Switch, Typography } from '@shared/ui';
import type React from 'react';
import AvailabilityWindowFields from './AvailabilityWindowFields';
import type { EditableAvailabilityDay, EditableAvailabilityWindow } from './availabilityTransforms';
import { WEEKDAY_LABELS } from './availabilityTransforms';
import type { AvailabilityWeekday } from './availabilityTypes';

interface AvailabilityDayRowProps {
  day: EditableAvailabilityDay;
  errors?: string[];
  disabled?: boolean;
  onToggle: (enabled: boolean) => void;
  onAddWindow: () => void;
  onWindowChange: (window: EditableAvailabilityWindow) => void;
  onRemoveWindow: (windowID: string) => void;
  onCopy: (weekday: AvailabilityWeekday, anchorEl: HTMLElement) => void;
}

const AvailabilityDayRow: React.FC<AvailabilityDayRowProps> = ({
  day,
  errors = [],
  disabled = false,
  onToggle,
  onAddWindow,
  onWindowChange,
  onRemoveWindow,
  onCopy,
}) => {
  return (
    <Box data-testid={`availability-day-${day.weekday}`} sx={{ py: 1.55 }}>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 2, minWidth: 0 }}>
        <Switch
          checked={day.enabled}
          disabled={disabled}
          inputProps={{ 'aria-label': `Toggle ${WEEKDAY_LABELS[day.weekday]} availability` }}
          onChange={(_, checked) => onToggle(checked)}
          sx={{ flexShrink: 0 }}
        />
        <Typography
          sx={{
            color: day.enabled ? '#262626' : '#A4A4A4',
            flex: '0 0 84px',
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 0.1,
            textTransform: 'uppercase',
          }}
        >
          {WEEKDAY_LABELS[day.weekday]}
        </Typography>

        {day.enabled ? (
          <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 1, minWidth: 0 }}>
            {day.windows.map((window) => (
              <Box key={window.id} sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }}>
                <AvailabilityWindowFields
                  window={window}
                  disabled={disabled}
                  onChange={onWindowChange}
                />
                <IconButton
                  type="button"
                  variant="text"
                  size="small"
                  disabled={disabled || day.windows.length <= 1}
                  aria-label="Remove window"
                  onClick={() => onRemoveWindow(window.id)}
                  sx={{ flexShrink: 0 }}
                >
                  <Icon>remove_circle</Icon>
                </IconButton>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography sx={{ color: '#A4A4A4', flex: 1, fontSize: 14, minWidth: 0 }}>
            Unavailable
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexShrink: 0, gap: 1.25 }}>
          <IconButton
            type="button"
            variant="text"
            size="small"
            disabled={disabled || !day.enabled}
            aria-label="Add window"
            onClick={onAddWindow}
          >
            <Icon>add_circle</Icon>
          </IconButton>
          <IconButton
            type="button"
            variant="text"
            size="small"
            disabled={disabled}
            aria-label="Copy"
            onClick={(event) => onCopy(day.weekday, event.currentTarget)}
          >
            <Icon>content_copy</Icon>
          </IconButton>
        </Box>
      </Box>

      {errors.length > 0 ? (
        <Box sx={{ ml: { xs: 0, sm: 16 }, mt: 1 }}>
          {errors.map((error) => (
            <Typography key={error} sx={{ color: 'error.main', fontSize: 12 }}>
              {error}
            </Typography>
          ))}
        </Box>
      ) : null}
    </Box>
  );
};

export default AvailabilityDayRow;
